import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/credits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Megaphone, Send, Eye, Paperclip, X } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";
const PRIORITY_COLOR = { normal: "text-neutral-400", important: "text-amber-400", urgent: "text-[#FF6069]" };
const AUDIENCE_LABEL = { all: "All Installers", active: "Active", pending: "Pending", suspended: "Suspended", low_credits: "Low Credits", selected: "Selected" };
const STATUS_COLOR = { sent: "text-emerald-400", partially_sent: "text-amber-400", failed: "text-[#FF6069]", scheduled: "text-sky-400", expired: "text-neutral-500", sending: "text-neutral-400", draft: "text-neutral-500" };
const STATUS_LABEL = { sent: "Sent", partially_sent: "Partially Sent", failed: "Failed", scheduled: "Scheduled", expired: "Expired", sending: "Sending", draft: "Draft" };
const TEMPLATES = [
  { value: "new_product", label: "New Product" }, { value: "product_update", label: "Product Update" },
  { value: "software_update", label: "Software Update" }, { value: "training", label: "Training Announcement" },
  { value: "general", label: "General Announcement" }, { value: "maintenance", label: "Maintenance Notice" },
  { value: "alert", label: "Important Alert" },
];

const blankForm = { subject: "", message: "", priority: "normal", audience: "active", template: "", cta_label: "", cta_url: "", publication_date: "", expiration_date: "", methods_dashboard: true, methods_email: false };

export default function Broadcast() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [templates, setTemplates] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/broadcasts").then(({ data }) => setHistory(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/broadcast-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (t) => {
    const tpl = templates[t];
    setForm((f) => ({ ...f, template: t, subject: tpl?.subject || f.subject, message: tpl?.message || f.message }));
  };

  const buildPayload = () => {
    const methods = [];
    if (form.methods_dashboard) methods.push("dashboard");
    if (form.methods_email) methods.push("email");
    return {
      subject: form.subject, message: form.message, priority: form.priority, audience: form.audience,
      template: form.template, cta_label: form.cta_label, cta_url: form.cta_url,
      publication_date: form.publication_date || null, expiration_date: form.expiration_date || null, methods,
      attachments,
    };
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/broadcasts/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, data]);
      toast.success(`Attached ${data.filename}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Attachment upload failed"); }
    finally { setUploadingAtt(false); }
  };
  const removeAttachment = (path) => setAttachments((a) => a.filter((x) => x.path !== path));

  const validate = () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Subject and message are required"); return false; }
    if (!form.methods_dashboard && !form.methods_email) { toast.error("Select at least one delivery method"); return false; }
    return true;
  };

  const openPreview = async () => {
    if (!validate()) return;
    try {
      const { data } = await api.post("/admin/broadcasts/preview", buildPayload());
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to preview"); }
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcasts", buildPayload());
      if (data.status === "scheduled") toast.success(`Broadcast scheduled for ${data.recipients} installer(s)`);
      else toast.success(`Broadcast sent to ${data.recipients} installer(s)`);
      setForm(blankForm);
      setAttachments([]);
      setPreviewOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <DashboardLayout title="Broadcast Center">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Communications</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Broadcast Center</h2>
        <p className="mt-1 text-sm text-neutral-500">Send announcements to your installer network.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6 xl:col-span-2" data-testid="broadcast-compose">
          <div className="mb-5 flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#FF1C2A]" /><h3 className="font-display text-base font-medium text-white">New Broadcast</h3></div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-300">Template (optional)</Label>
              <Select value={form.template} onValueChange={applyTemplate}>
                <SelectTrigger className={inputCls} data-testid="broadcast-template"><SelectValue placeholder="Start from a template" /></SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                  {TEMPLATES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="text-neutral-300">Subject *</Label><Input value={form.subject} onChange={(e) => set("subject")(e.target.value)} className={inputCls} data-testid="broadcast-subject" /></div>
            <div className="space-y-2"><Label className="text-neutral-300">Message *</Label><Textarea value={form.message} onChange={(e) => set("message")(e.target.value)} rows={6} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="broadcast-message" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-neutral-300">Audience</Label>
                <Select value={form.audience} onValueChange={set("audience")}>
                  <SelectTrigger className={inputCls} data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                    <SelectItem value="all">All Installers</SelectItem>
                    <SelectItem value="active">Active Installers</SelectItem>
                    <SelectItem value="pending">Pending Installers</SelectItem>
                    <SelectItem value="suspended">Suspended Installers</SelectItem>
                    <SelectItem value="low_credits">Low Credits</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">Priority</Label>
                <Select value={form.priority} onValueChange={set("priority")}>
                  <SelectTrigger className={inputCls} data-testid="broadcast-priority"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="important">Important</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-neutral-300">CTA Label (optional)</Label><Input value={form.cta_label} onChange={(e) => set("cta_label")(e.target.value)} placeholder="Learn More" className={inputCls} data-testid="broadcast-cta-label" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">CTA URL (optional)</Label><Input value={form.cta_url} onChange={(e) => set("cta_url")(e.target.value)} placeholder="https://…" className={inputCls} data-testid="broadcast-cta-url" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-neutral-300">Publish Date (optional)</Label><Input type="date" value={form.publication_date} onChange={(e) => set("publication_date")(e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="broadcast-publication" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Expiration Date (optional)</Label><Input type="date" value={form.expiration_date} onChange={(e) => set("expiration_date")(e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="broadcast-expiration" /></div>
            </div>
            <div className="flex gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.methods_dashboard} onChange={(e) => set("methods_dashboard")(e.target.checked)} className="accent-[#E30613]" data-testid="broadcast-method-dashboard" /> Dashboard</label>
              <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.methods_email} onChange={(e) => set("methods_email")(e.target.checked)} className="accent-[#E30613]" data-testid="broadcast-method-email" /> Email</label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-neutral-300">Email Attachments {!form.methods_email && <span className="text-[11px] text-neutral-600">(enable Email to include)</span>}</Label>
                <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-white/[0.05] ${uploadingAtt ? "opacity-60" : ""}`} data-testid="broadcast-attach-label">
                  {uploadingAtt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Add file
                  <input type="file" onChange={uploadAttachment} disabled={uploadingAtt} className="hidden" data-testid="broadcast-attach-input" />
                </label>
              </div>
              {attachments.length > 0 && (
                <div className="space-y-1.5" data-testid="broadcast-attachments">
                  {attachments.map((a) => (
                    <div key={a.path} className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2 text-xs" data-testid={`attachment-${a.filename}`}>
                      <span className="flex items-center gap-2 truncate text-neutral-300"><Paperclip className="h-3.5 w-3.5 shrink-0 text-neutral-500" /> <span className="truncate">{a.filename}</span> <span className="shrink-0 text-neutral-600">{(a.size / 1024).toFixed(0)} KB</span></span>
                      <button onClick={() => removeAttachment(a.path)} className="ml-2 shrink-0 text-neutral-500 hover:text-[#FF6069]" data-testid={`remove-attachment-${a.filename}`}><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={openPreview} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#E30613] px-5 py-3 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="preview-broadcast-btn"><Eye className="h-4 w-4" /> Preview & Send</button>
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6 xl:col-span-3" data-testid="broadcast-history">
          <h3 className="mb-5 font-display text-base font-medium text-white">Broadcast History</h3>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : history.length === 0 ? (
            <p className="py-10 text-center text-sm text-neutral-500">No broadcasts sent yet.</p>
          ) : (
            <div className="space-y-3">
              {history.map((b) => (
                <div key={b.id} className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4" data-testid="broadcast-row">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-white">{b.subject} <span className={`ml-1 text-xs ${PRIORITY_COLOR[b.priority] || "text-neutral-400"}`}>· {b.priority}</span></p>
                      <p className="mt-1 line-clamp-2 text-xs text-neutral-500">{b.message}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-medium ${STATUS_COLOR[b.status] || "text-neutral-400"}`}>{STATUS_LABEL[b.status] || b.status}</span>
                      <p className="whitespace-nowrap text-xs text-neutral-600">{fmtDate(b.created_at, true)}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-neutral-500">
                    <span>Audience: <span className="text-neutral-300">{AUDIENCE_LABEL[b.audience] || b.audience}</span></span>
                    <span>Recipients: <span className="text-neutral-300">{b.recipients}</span></span>
                    <span>Dashboard: <span className="text-emerald-400">{b.dashboard_delivered}</span></span>
                    <span>Email: <span className="text-emerald-400">{b.email_sent}</span>{b.email_failed ? <span className="text-[#FF6069]"> / {b.email_failed} failed</span> : ""}</span>
                    {b.publication_date && <span>Publish: <span className="text-neutral-300">{fmtDate(b.publication_date)}</span></span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg border-white/[0.08] bg-[#141414] text-white" data-testid="broadcast-preview-modal">
          <DialogHeader>
            <DialogTitle>Confirm Broadcast</DialogTitle>
            <DialogDescription className="text-neutral-500">Review the recipients and message before sending.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4 text-sm">
                <span className="text-neutral-500">Audience: <span className="text-white">{AUDIENCE_LABEL[preview.audience] || preview.audience}</span></span>
                <span className="text-neutral-500">Recipients: <span className="font-semibold text-[#FF6069]" data-testid="preview-recipient-count">{preview.recipient_count}</span></span>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">Email Preview</p>
                <iframe title="broadcast-email-preview" srcDoc={preview.email_html} sandbox="" data-testid="preview-email" className="h-52 w-full rounded-xl border border-white/[0.06] bg-[#050505]" />
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">Dashboard Notification</p>
                <p className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-3 text-sm text-neutral-300" data-testid="preview-notification">{preview.notification_text}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setPreviewOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={confirmSend} disabled={sending || preview?.recipient_count === 0} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="send-broadcast-btn">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Confirm & Send</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
